import React, { useState, useEffect, useCallback } from 'react';
import * as api from './api';
import CoachView from './CoachView';
import './App.css';

// profileId resolved from /me on mount — see App state below

// ==================== Analytics ====================

const RECOVERY_COLORS = {
  high_risk:        '#f87171',
  partial_recovery: '#f97316',
  trainable:        '#fbbf24',
  fully_recovered:  '#22c55e',
  not_trained:      '#1e293b',
};

function getRecoveryColor(statusMap, id) {
  return RECOVERY_COLORS[statusMap[id]] || RECOVERY_COLORS.not_trained;
}

function BodyMap({ muscleRecovery }) {
  const statusMap = {};
  (muscleRecovery || []).forEach(m => { statusMap[m.svg_id] = m.status; });
  const c = id => getRecoveryColor(statusMap, id);

  return (
    <svg viewBox="0 0 300 460" width="100%" style={{ maxWidth: 360, display: 'block', margin: '0 auto' }}>
      <line x1="150" y1="0" x2="150" y2="460" stroke="#1e293b" strokeWidth="1"/>
      <text x="75" y="14" textAnchor="middle" fontSize="9" fill="#475569" letterSpacing="1.5">FRONT</text>
      <text x="225" y="14" textAnchor="middle" fontSize="9" fill="#475569" letterSpacing="1.5">BACK</text>

      {/* Head + neck — static */}
      <circle cx="75" cy="22" r="14" fill="#0f172a" stroke="#334155" strokeWidth="1"/>
      <path d="M 70,36 L 69,46 L 81,46 L 80,36 Z" fill="#0f172a"/>
      <circle cx="225" cy="22" r="14" fill="#0f172a" stroke="#334155" strokeWidth="1"/>
      <path d="M 220,36 L 219,46 L 231,46 L 230,36 Z" fill="#0f172a"/>

      {/* Front: traps */}
      <path d="M 52,48 C 44,44 40,54 42,64 C 44,72 52,75 60,73 C 67,71 70,63 68,55 C 66,49 60,47 52,48 Z" fill={c('traps_front')}/>
      <path d="M 98,48 C 106,44 110,54 108,64 C 106,72 98,75 90,73 C 83,71 80,63 82,55 C 84,49 90,47 98,48 Z" fill={c('traps_front')}/>

      {/* Front: anterior deltoids */}
      <path d="M 37,57 C 27,55 23,70 25,86 C 27,100 37,106 47,104 C 55,102 59,90 57,76 C 55,64 47,57 37,57 Z" fill={c('anterior_deltoid')}/>
      <path d="M 113,57 C 123,55 127,70 125,86 C 123,100 113,106 103,104 C 95,102 91,90 93,76 C 95,64 103,57 113,57 Z" fill={c('anterior_deltoid')}/>

      {/* Front: chest */}
      <path d="M 56,65 C 45,63 40,78 42,94 C 44,108 54,114 64,112 C 72,110 76,98 74,84 C 72,72 64,65 56,65 Z" fill={c('chest')}/>
      <path d="M 94,65 C 105,63 110,78 108,94 C 106,108 96,114 86,112 C 78,110 74,98 76,84 C 78,72 86,65 94,65 Z" fill={c('chest')}/>

      {/* Front: biceps */}
      <path d="M 27,92 C 17,92 11,108 13,128 C 15,146 25,154 35,152 C 43,150 47,138 45,122 C 43,106 35,92 27,92 Z" fill={c('biceps')}/>
      <path d="M 123,92 C 133,92 139,108 137,128 C 135,146 125,154 115,152 C 107,150 103,138 105,122 C 107,106 115,92 123,92 Z" fill={c('biceps')}/>

      {/* Front: forearms */}
      <path d="M 19,154 C 9,156 5,172 7,192 C 9,210 19,218 29,216 C 37,214 41,200 39,182 C 37,164 29,154 19,154 Z" fill={c('forearms')}/>
      <path d="M 131,154 C 141,156 145,172 143,192 C 141,210 131,218 121,216 C 113,214 109,200 111,182 C 113,164 121,154 131,154 Z" fill={c('forearms')}/>

      {/* Front: abs */}
      <path d="M 60,114 C 53,112 49,128 49,148 C 49,166 53,180 61,184 C 67,188 71,190 75,190 C 79,190 83,188 89,184 C 97,180 101,166 101,148 C 101,128 97,112 90,114 C 85,116 80,118 75,118 C 70,118 65,116 60,114 Z" fill={c('abs')}/>

      {/* Front: obliques */}
      <path d="M 47,116 C 41,114 37,130 37,150 C 37,168 41,182 47,186 C 51,188 53,182 53,168 C 53,154 51,138 47,124 Z" fill={c('obliques')}/>
      <path d="M 103,116 C 109,114 113,130 113,150 C 113,168 109,182 103,186 C 99,188 97,182 97,168 C 97,154 99,138 103,124 Z" fill={c('obliques')}/>

      {/* Front: quads */}
      <path d="M 43,196 C 29,196 22,218 22,252 C 22,284 28,316 41,332 C 49,342 57,342 63,338 C 69,334 71,318 69,294 C 67,270 61,242 51,222 C 47,210 45,196 43,196 Z" fill={c('quads')}/>
      <path d="M 107,196 C 121,196 128,218 128,252 C 128,284 122,316 109,332 C 101,342 93,342 87,338 C 81,334 79,318 81,294 C 83,270 89,242 99,222 C 103,210 105,196 107,196 Z" fill={c('quads')}/>

      {/* Front: calves */}
      <path d="M 33,336 C 19,338 12,360 14,386 C 16,410 28,420 40,418 C 50,416 54,400 52,376 C 50,352 42,338 33,336 Z" fill={c('calves')}/>
      <path d="M 117,336 C 131,338 138,360 136,386 C 134,410 122,420 110,418 C 100,416 96,400 98,376 C 100,352 108,338 117,336 Z" fill={c('calves')}/>

      {/* Feet — static */}
      <path d="M 10,420 C 2,420 0,428 8,434 C 16,440 34,440 44,434 C 52,428 52,420 44,420 Z" fill="#0f172a" stroke="#334155" strokeWidth="0.5"/>
      <path d="M 140,420 C 148,420 150,428 142,434 C 134,440 116,440 106,434 C 98,428 98,420 106,420 Z" fill="#0f172a" stroke="#334155" strokeWidth="0.5"/>

      {/* Back: trapezius */}
      <path d="M 198,46 C 188,42 182,54 184,68 C 186,80 196,86 208,84 C 216,82 220,72 218,60 C 216,50 208,46 198,46 Z" fill={c('trapezius')}/>
      <path d="M 252,46 C 262,42 268,54 266,68 C 264,80 254,86 242,84 C 234,82 230,72 232,60 C 234,50 242,46 252,46 Z" fill={c('trapezius')}/>

      {/* Back: posterior deltoids */}
      <path d="M 185,57 C 175,55 171,70 173,86 C 175,100 185,106 195,104 C 203,102 207,90 205,76 C 203,64 195,57 185,57 Z" fill={c('posterior_deltoid')}/>
      <path d="M 265,57 C 275,55 279,70 277,86 C 275,100 265,106 255,104 C 247,102 243,90 245,76 C 247,64 255,57 265,57 Z" fill={c('posterior_deltoid')}/>

      {/* Back: lats */}
      <path d="M 183,96 C 171,102 167,126 169,150 C 171,170 179,182 189,186 C 195,188 199,180 197,166 C 195,152 191,134 187,118 C 185,108 184,96 183,96 Z" fill={c('lats')}/>
      <path d="M 267,96 C 279,102 283,126 281,150 C 279,170 271,182 261,186 C 255,188 251,180 253,166 C 255,152 259,134 263,118 C 265,108 266,96 267,96 Z" fill={c('lats')}/>

      {/* Back: triceps */}
      <path d="M 176,90 C 166,90 160,106 162,128 C 164,148 174,156 184,154 C 192,152 196,140 194,122 C 192,106 184,90 176,90 Z" fill={c('triceps')}/>
      <path d="M 274,90 C 284,90 290,106 288,128 C 286,148 276,156 266,154 C 258,152 254,140 256,122 C 258,106 266,90 274,90 Z" fill={c('triceps')}/>

      {/* Back: forearms */}
      <path d="M 168,156 C 158,158 154,174 156,194 C 158,212 168,220 178,218 C 186,216 190,202 188,184 C 186,166 178,156 168,156 Z" fill={c('forearms')}/>
      <path d="M 282,156 C 292,158 296,174 294,194 C 292,212 282,220 272,218 C 264,216 260,202 262,184 C 264,166 272,156 282,156 Z" fill={c('forearms')}/>

      {/* Back: lower back */}
      <path d="M 207,118 C 200,116 196,132 196,152 C 196,170 200,184 208,190 C 214,194 219,196 225,196 C 231,196 236,194 242,190 C 250,184 254,170 254,152 C 254,132 250,116 243,118 C 237,120 232,122 225,122 C 218,122 213,120 207,118 Z" fill={c('lower_back')}/>

      {/* Back: glutes */}
      <path d="M 196,198 C 182,198 174,218 174,244 C 174,268 182,284 196,288 C 206,290 214,282 216,264 C 218,246 216,226 210,212 C 206,202 201,198 196,198 Z" fill={c('glutes')}/>
      <path d="M 254,198 C 268,198 276,218 276,244 C 276,268 268,284 254,288 C 244,290 236,282 234,264 C 232,246 234,226 240,212 C 244,202 249,198 254,198 Z" fill={c('glutes')}/>

      {/* Back: hamstrings */}
      <path d="M 191,288 C 177,290 168,314 168,348 C 168,376 176,396 188,402 C 198,408 208,404 212,388 C 216,372 214,346 208,320 C 204,300 198,288 191,288 Z" fill={c('hamstrings')}/>
      <path d="M 259,288 C 273,290 282,314 282,348 C 282,376 274,396 262,402 C 252,408 242,404 238,388 C 234,372 236,346 242,320 C 246,300 252,288 259,288 Z" fill={c('hamstrings')}/>

      {/* Back: calves */}
      <path d="M 182,402 C 168,404 160,426 162,450 C 163,458 170,460 178,458 C 188,456 196,444 196,428 C 196,412 190,402 182,402 Z" fill={c('calves')}/>
      <path d="M 268,402 C 282,404 290,426 288,450 C 287,458 280,460 272,458 C 262,456 254,444 254,428 C 254,412 260,402 268,402 Z" fill={c('calves')}/>

      {/* Back: feet — static */}
      <path d="M 158,452 C 150,452 148,460 156,464 C 164,468 182,468 192,462 C 200,456 200,452 192,452 Z" fill="#0f172a" stroke="#334155" strokeWidth="0.5"/>
      <path d="M 292,452 C 300,452 302,460 294,464 C 286,468 268,468 258,462 C 250,456 250,452 258,452 Z" fill="#0f172a" stroke="#334155" strokeWidth="0.5"/>
    </svg>
  );
}

function MiniLineChart({ data, color = '#f97316' }) {
  if (!data || data.length === 0) return <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 12 }}>No data yet</div>;
  const vals = data.map(d => d.value);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  const w = 300, h = 80, pad = 4;
  const points = vals.map((v, i) => {
    const x = pad + (i / Math.max(vals.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  const areaPoints = `${pad},${h} ${points} ${w - pad},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%' }}>
      <defs>
        <linearGradient id={`cg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#cg-${color.replace('#','')})`}/>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2"/>
      {vals.map((v, i) => {
        const x = pad + (i / Math.max(vals.length - 1, 1)) * (w - pad * 2);
        const y = h - pad - ((v - min) / range) * (h - pad * 2);
        return <circle key={i} cx={x} cy={y} r="3" fill={color}/>;
      })}
    </svg>
  );
}

function MiniBarChart({ data, color = '#6366f1' }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const w = 300, h = 60, pad = 4;
  const barW = Math.max(2, (w - pad * 2) / data.length - 3);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%' }}>
      {data.map((d, i) => {
        const barH = Math.max(2, (d.value / max) * (h - pad * 2 - 14));
        const x = pad + i * ((w - pad * 2) / data.length);
        return (
          <g key={i}>
            <rect x={x} y={h - pad - 14 - barH} width={barW} height={barH} rx="2" fill={color} opacity="0.8"/>
            <text x={x + barW / 2} y={h - 2} textAnchor="middle" fontSize="7" fill="#475569">{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function MacroCircle({ pct, label, grams, color }) {
  const r = 28, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#1e293b" strokeWidth="8"/>
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 36 36)"/>
        <text x="36" y="40" textAnchor="middle" fontSize="13" fontWeight="800" fill={color}>{pct}%</text>
      </svg>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#f0f4f8', marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: 11, color: '#64748b' }}>{grams}g avg</div>
    </div>
  );
}

function AnalyticsView({ profileId }) {
  const [tab, setTab] = useState('workout');
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [calPattern, setCalPattern] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bodyView, setBodyView] = useState('map'); // 'map' | 'list'

  useEffect(() => {
    if (!profileId) return;
    setLoading(true);
    Promise.all([
      api.getAnalytics(profileId, period),
      api.getCaloriePattern(profileId, 5).catch(() => null),
    ])
      .then(([analytics, pattern]) => { setData(analytics); setCalPattern(pattern); })
      .catch(e => console.error('Analytics load failed:', e))
      .finally(() => setLoading(false));
  }, [profileId, period]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading analytics…</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No data available.</div>;

  const { workout, health, nutrition } = data;

  const TAB_STYLE = active => ({
    flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 700,
    cursor: 'pointer', background: active ? '#f97316' : '#111820', color: active ? '#fff' : '#64748b',
  });

  const CARD = { background: '#111820', borderRadius: 14, padding: 16, marginBottom: 12 };
  const STAT_CARD = (borderColor) => ({
    flex: 1, background: '#111820', borderRadius: 12, padding: 14, borderLeft: `3px solid ${borderColor}`,
  });
  const SEC_TITLE = { fontSize: 18, fontWeight: 700, color: '#f0f4f8', marginBottom: 12, marginTop: 22 };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 16px 60px' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, margin: '20px 0 4px' }}>
        <button style={TAB_STYLE(tab === 'workout')}   onClick={() => setTab('workout')}>💪 WORKOUT</button>
        <button style={TAB_STYLE(tab === 'health')}    onClick={() => setTab('health')}>❤️ HEALTH</button>
        <button style={TAB_STYLE(tab === 'nutrition')} onClick={() => setTab('nutrition')}>🥗 NUTRITION</button>
      </div>

      {/* ── WORKOUT TAB ── */}
      {tab === 'workout' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <div style={STAT_CARD('#6366f1')}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f0f4f8' }}>{workout.streak}d</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Streak</div>
            </div>
            <div style={STAT_CARD('#f97316')}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f0f4f8' }}>{workout.total_all_time}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Total Workouts</div>
            </div>
            <div style={STAT_CARD('#22c55e')}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f0f4f8' }}>{workout.total_this_month}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>This Month</div>
            </div>
          </div>

          {/* Training Volume */}
          <div style={SEC_TITLE}>Training Volume</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {['week', 'month', 'year'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding: '5px 14px', borderRadius: 20, border: '1px solid',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: period === p ? 'rgba(249,115,22,.15)' : '#1a2233',
                borderColor: period === p ? '#f97316' : 'rgba(255,255,255,.07)',
                color: period === p ? '#f97316' : '#94a3b8',
              }}>{p.charAt(0).toUpperCase() + p.slice(1)}</button>
            ))}
          </div>
          <div style={CARD}>
            <MiniLineChart data={workout.volume_trend} color="#f97316"/>
            {workout.volume_trend.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: '#475569' }}>
                {workout.volume_trend.map((d, i) => <span key={i}>{d.label}</span>)}
              </div>
            )}
          </div>

          {/* Personal Bests */}
          <div style={SEC_TITLE}>Personal Bests</div>
          <div style={{ background: '#111820', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
            {workout.personal_bests.length === 0
              ? <div style={{ padding: 16, color: '#475569', fontSize: 13 }}>No logged workouts yet</div>
              : workout.personal_bests.map((pb, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: i < workout.personal_bests.length - 1 ? '1px solid rgba(255,255,255,.07)' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#f0f4f8' }}>{pb.exercise_name}</div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{pb.reps} reps × {pb.weight} lbs</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#6366f1' }}>{pb.volume.toLocaleString()} lbs</div>
                </div>
              ))
            }
          </div>

          {/* Muscle Recovery */}
          <div style={SEC_TITLE}>Muscle Recovery</div>
          <div style={CARD}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[['map', 'Body Map'], ['list', 'List View']].map(([v, label]) => (
                <button key={v} onClick={() => setBodyView(v)} style={{
                  padding: '5px 14px', borderRadius: 20, border: '1px solid',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: bodyView === v ? 'rgba(249,115,22,.15)' : '#1a2233',
                  borderColor: bodyView === v ? '#f97316' : 'rgba(255,255,255,.07)',
                  color: bodyView === v ? '#f97316' : '#94a3b8',
                }}>{label}</button>
              ))}
            </div>

            {bodyView === 'map' ? (
              <>
                <BodyMap muscleRecovery={workout.muscle_recovery}/>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14, justifyContent: 'center' }}>
                  {[['high_risk','High Risk'],['partial_recovery','Partial Recovery'],['trainable','Trainable'],['fully_recovered','Fully Recovered'],['not_trained','Not Trained']].map(([status, label]) => (
                    <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: RECOVERY_COLORS[status], border: status === 'not_trained' ? '1px solid #334155' : 'none' }}/>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{label}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div>
                {workout.muscle_recovery.filter(m => m.status !== 'not_trained').sort((a, b) => (a.days_ago ?? 999) - (b.days_ago ?? 999)).map((m, i, arr) => (
                  <div key={m.svg_id} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,.07)' : 'none' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: RECOVERY_COLORS[m.status], marginRight: 12, flexShrink: 0 }}/>
                    <span style={{ flex: 1, fontSize: 13, color: '#f0f4f8' }}>{m.svg_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    <span style={{ fontSize: 11, color: '#64748b', marginRight: 12 }}>{m.days_ago === 0 ? 'Today' : m.days_ago === 1 ? '1d ago' : `${m.days_ago}d ago`}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: RECOVERY_COLORS[m.status] }}>
                      {m.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                ))}
                {workout.muscle_recovery.every(m => m.status === 'not_trained') && (
                  <div style={{ color: '#475569', fontSize: 13 }}>No muscle data yet — complete some workouts first.</div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── HEALTH TAB ── */}
      {tab === 'health' && (
        <>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <div style={STAT_CARD('#22c55e')}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f0f4f8' }}>{health.steps_today.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Steps Today</div>
            </div>
            <div style={STAT_CARD('#6366f1')}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f0f4f8' }}>{health.steps_this_week.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>This Week</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <div style={STAT_CARD('#f97316')}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f0f4f8' }}>{health.steps_daily_avg_30d.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Daily Avg (30d)</div>
            </div>
            <div style={STAT_CARD('#f87171')}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f0f4f8' }}>{health.calories_burned_today.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Cal Burned Today</div>
            </div>
          </div>

          <div style={SEC_TITLE}>Daily Steps (7 days)</div>
          <div style={CARD}>
            <MiniBarChart data={health.daily_steps} color="#22c55e"/>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <div style={STAT_CARD('#6366f1')}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f0f4f8' }}>{health.resting_hr ?? '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Resting HR (bpm)</div>
            </div>
            <div style={STAT_CARD('#22c55e')}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f0f4f8' }}>{health.sleep_hours ?? '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Sleep (hours)</div>
            </div>
          </div>

          {health.steps_today === 0 && health.resting_hr == null && (
            <div style={{ marginTop: 24, color: '#475569', fontSize: 13, textAlign: 'center' }}>
              No health data yet. Health metrics sync from Apple Health on the mobile app.
            </div>
          )}
        </>
      )}

      {/* ── NUTRITION TAB ── */}
      {tab === 'nutrition' && (
        <>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <div style={STAT_CARD('#6366f1')}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f0f4f8' }}>{nutrition.calorie_streak}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Day Streak</div>
            </div>
            <div style={STAT_CARD('#f97316')}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#f0f4f8', marginTop: 4 }}>{nutrition.last_logged ?? '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Last Logged</div>
            </div>
          </div>

          <div style={SEC_TITLE}>Daily Calories (30 days)</div>
          <div style={CARD}>
            {nutrition.calorie_goal && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 10, color: '#475569', marginBottom: 4 }}>
                Target: {nutrition.calorie_goal.toLocaleString()} cal
              </div>
            )}
            <MiniLineChart data={nutrition.daily_calories_30d.map(d => ({ label: d.date.slice(5), value: d.calories }))} color="#6366f1"/>
          </div>

          <div style={SEC_TITLE}>Macro Split (30d avg)</div>
          <div style={{ ...CARD, display: 'flex', justifyContent: 'space-around', padding: '20px 8px' }}>
            <MacroCircle pct={nutrition.macro_avg_30d.protein_pct} label="Protein" grams={nutrition.macro_avg_30d.protein_g} color="#f97316"/>
            <MacroCircle pct={nutrition.macro_avg_30d.carbs_pct}   label="Carbs"   grams={nutrition.macro_avg_30d.carbs_g}   color="#6366f1"/>
            <MacroCircle pct={nutrition.macro_avg_30d.fat_pct}     label="Fat"     grams={nutrition.macro_avg_30d.fat_g}     color="#f87171"/>
          </div>

          {nutrition.avg_by_meal.length > 0 && (
            <>
              <div style={SEC_TITLE}>Avg Calories by Meal</div>
              <div style={CARD}>
                {nutrition.avg_by_meal.map((m, i) => {
                  const maxCal = Math.max(...nutrition.avg_by_meal.map(x => x.avg_calories), 1);
                  return (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{m.meal_name}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#6366f1' }}>{m.avg_calories} cal</span>
                      </div>
                      <div style={{ background: '#1a2233', borderRadius: 6, height: 6 }}>
                        <div style={{ height: 6, borderRadius: 6, background: '#6366f1', width: `${(m.avg_calories / maxCal) * 100}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {calPattern && calPattern.days && calPattern.days.length > 0 && (() => {
            const maxVal = Math.max(...calPattern.days.map(d => Math.max(d.avg_intake || 0, d.avg_burned || 0, calPattern.calorie_target || 0)), 1);
            return (
              <>
                <div style={SEC_TITLE}>Weekly Energy Pattern</div>
                {calPattern.summary && (
                  <div style={{ background: '#1a2233', borderLeft: '3px solid #f97316', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>
                    {calPattern.summary}
                  </div>
                )}
                <div style={CARD}>
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginBottom: 12, fontSize: 10, color: '#475569' }}>
                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#6366f1', marginRight: 4 }}/>Eaten</span>
                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#22c55e', marginRight: 4 }}/>Burned</span>
                    {calPattern.calorie_target > 0 && <span><span style={{ display: 'inline-block', width: 10, height: 2, background: '#475569', marginRight: 4, verticalAlign: 'middle' }}/>Target</span>}
                  </div>
                  {calPattern.days.map((d, i) => {
                    const flagged = d.days_over_threshold >= 2;
                    const isOpen = expandedDay === d.day_of_week;
                    const targetPct = calPattern.calorie_target ? Math.min(100, (calPattern.calorie_target / maxVal) * 100) : null;
                    return (
                      <div key={i} style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => setExpandedDay(isOpen ? null : d.day_of_week)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 30, fontSize: 12, color: flagged ? '#f87171' : '#64748b', fontWeight: flagged ? 700 : 400 }}>
                            {d.day_name.slice(0, 3)}
                          </span>
                          <div style={{ flex: 1, position: 'relative' }}>
                            <div style={{ height: 8, background: '#0f172a', borderRadius: 4, marginBottom: 3, overflow: 'hidden' }}>
                              <div style={{ height: 8, borderRadius: 4, background: '#6366f1', width: `${Math.min(100, (d.avg_intake / maxVal) * 100)}%` }} />
                            </div>
                            {d.avg_burned !== null && (
                              <div style={{ height: 8, background: '#0f172a', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ height: 8, borderRadius: 4, background: '#22c55e', width: `${Math.min(100, (d.avg_burned / maxVal) * 100)}%` }} />
                              </div>
                            )}
                            {targetPct !== null && (
                              <div style={{ position: 'absolute', left: `${targetPct}%`, top: 0, bottom: 0, width: 1.5, background: '#475569aa' }} />
                            )}
                          </div>
                          <span style={{ width: 44, textAlign: 'right', fontSize: 11, color: flagged ? '#f87171' : '#475569', fontWeight: flagged ? 700 : 400 }}>
                            {d.avg_intake.toLocaleString()}
                          </span>
                        </div>
                        {isOpen && (
                          <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px', marginTop: 4, marginLeft: 38, fontSize: 11, color: '#94a3b8', lineHeight: 1.8 }}>
                            <div>Avg eaten: {d.avg_intake.toLocaleString()} kcal</div>
                            {d.avg_burned !== null && <div>Avg burned: {d.avg_burned.toLocaleString()} kcal</div>}
                            {d.avg_net !== null && <div>Avg net: {d.avg_net > 0 ? '+' : ''}{d.avg_net.toLocaleString()} kcal</div>}
                            {calPattern.calorie_target > 0 && <div>Target: {calPattern.calorie_target.toLocaleString()} kcal</div>}
                            <div style={{ color: '#475569', marginTop: 4 }}>
                              Logged {d.days_logged} of {calPattern.weeks_analyzed} weeks
                              {d.days_over_threshold >= 2 ? ` · exceeded deficit ${d.days_over_threshold}×` : ''}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 10, color: '#334155', textAlign: 'right', marginTop: 8 }}>
                    Averaged over {calPattern.weeks_analyzed} weeks · click a day for detail
                  </div>
                </div>
              </>
            );
          })()}

          {nutrition.calorie_streak === 0 && nutrition.daily_calories_30d.length === 0 && (
            <div style={{ marginTop: 24, color: '#475569', fontSize: 13, textAlign: 'center' }}>
              No nutrition data yet. Start logging meals in the app.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== Exercise Search Modal ====================
const WEIGHT_TYPES = ['Bands - Flat', 'Bands - Loop', 'Bands - Tube', 'Barbell', 'Body Weight', 'Cables', 'Cardio', 'Dumbell - Double', 'Dumbell - Single', 'Foam Rolling', 'Kettlebell', 'Kettlebell - Double', 'Machine', 'Med Ball', 'Stability Ball', 'Stretch', 'TRX', 'TRX Single'];
// eslint-disable-next-line no-unused-vars
const BAND_COLORS = ['Yellow', 'Red', 'Green', 'Blue', 'Black', 'Purple', 'Orange'];

function ExerciseSearch({ onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newWeightType, setNewWeightType] = useState('Body Weight');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try { setResults(await api.searchExercises(query)); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  async function handleCreate() {
    if (!query.trim()) return;
    setCreating(true);
    try {
      const newEx = await api.createExercise(query.trim(), newWeightType);
      onSelect({ id: newEx.id, exercise_name: newEx.exercise_name, weight_type: newEx.weight_type });
    } catch (e) { alert('Create failed: ' + e.message); }
    finally { setCreating(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Search Exercises</h3>
          <button className="btn-icon" onClick={onClose}>&times;</button>
        </div>
        <input
          className="search-input"
          placeholder="Type exercise name (min 2 chars)..."
          value={query}
          onChange={e => { setQuery(e.target.value); setShowCreate(false); }}
          autoFocus
        />
        <div className="search-results">
          {loading && <div className="loading">Searching...</div>}
          {results.map(ex => (
            <div key={ex.id} className="search-result" onClick={() => onSelect(ex)}>
              <span className="result-name">{ex.exercise_name}</span>
              <div className="result-meta">
                {ex.weight_type && <span className="badge">{ex.weight_type}</span>}
                {ex.muscle_group && <span className="badge badge-muted">{ex.muscle_group}</span>}
              </div>
            </div>
          ))}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="empty">No exercises found for "{query}"</div>
          )}
          {query.length >= 2 && !showCreate && (
            <div className="create-exercise-row" onClick={() => setShowCreate(true)}>
              <span className="create-exercise-label">+ Create "{query.trim()}"</span>
              <span className="create-exercise-hint">Add a new exercise to your library</span>
            </div>
          )}
          {showCreate && (
            <div style={{padding: '12px', borderTop: '1px solid #1e293b'}}>
              <div style={{marginBottom: 8, fontWeight: 600}}>Create "{query.trim()}"</div>
              <label style={{fontSize: 12, color: '#999'}}>Weight Type</label>
              <select value={newWeightType} onChange={e => setNewWeightType(e.target.value)} style={{width: '100%', padding: 6, marginBottom: 8}}>
                {WEIGHT_TYPES.map(wt => <option key={wt} value={wt}>{wt}</option>)}
              </select>
              <button className="btn-sm btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create & Select'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== Block Creator Modal ====================
function BlockCreator({ onCreateBlock, onClose }) {
  const [exerciseCount, setExerciseCount] = useState(2);
  const [rounds, setRounds] = useState(3);
  const [blockType, setBlockType] = useState('Superset');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Block</h3>
          <button className="btn-icon" onClick={onClose}>&times;</button>
        </div>
        <div className="form-group">
          <label>Block Type</label>
          <select value={blockType} onChange={e => setBlockType(e.target.value)}>
            <option value="Superset">Superset</option>
            <option value="Circuit">Circuit</option>
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Exercises</label>
            <input type="number" min={2} max={8} value={exerciseCount} onChange={e => setExerciseCount(+e.target.value)} />
          </div>
          <div className="form-group">
            <label>Rounds</label>
            <input type="number" min={1} max={10} value={rounds} onChange={e => setRounds(+e.target.value)} />
          </div>
        </div>
        <button className="btn-primary full-width" onClick={() => { onCreateBlock(exerciseCount, rounds, blockType); onClose(); }}>
          Create {blockType}
        </button>
      </div>
    </div>
  );
}

// ==================== Set Row ====================
function SetRow({ set, setIndex, isBand, isCardio, isBodyWeight, onChange, onDelete }) {
  const rowClass = isCardio ? "set-row set-row-cardio"
    : isBodyWeight ? "set-row set-row-bodyweight"
    : "set-row";
  return (
    <div className={rowClass}>
      <span className="set-num">{setIndex + 1}</span>
      {isCardio ? (
        <>
          <input className="set-input-cardio" type="number" placeholder="Sec" value={set.duration_seconds || ''}
            onChange={e => onChange({ ...set, duration_seconds: +e.target.value || null })} />
          <input className="set-input-cardio" type="number" placeholder="% effort" value={set.effort_pct || ''}
            onChange={e => onChange({ ...set, effort_pct: Math.min(+e.target.value || 0, 100) || null })} />
          <input className="set-input-cardio" type="number" placeholder="lbs" value={set.weight || ''}
            onChange={e => onChange({ ...set, weight: +e.target.value || null })} />
        </>
      ) : isBodyWeight ? (
        <>
          <input className="set-input" type="number" placeholder="Reps" value={set.reps || ''}
            onChange={e => onChange({ ...set, reps: +e.target.value || null })} />
          <input className="set-input" type="number" placeholder="Sec" value={set.duration_seconds || ''}
            onChange={e => onChange({ ...set, duration_seconds: +e.target.value || null })} />
          <input className="set-input" type="number" placeholder="% effort" value={set.effort_pct || ''}
            onChange={e => onChange({ ...set, effort_pct: Math.min(+e.target.value || 0, 100) || null })} />
          <input className="set-input" type="number" placeholder="lbs" value={set.weight || ''}
            onChange={e => onChange({ ...set, weight: +e.target.value || null })} />
        </>
      ) : (
        <>
          <input className="set-input" type="number" placeholder="Reps" value={set.reps || ''}
            onChange={e => onChange({ ...set, reps: +e.target.value || null })} />
          {isBand ? (
            <select className="set-input" value={set.band_color || ''} onChange={e => onChange({ ...set, band_color: e.target.value })}>
              <option value="">Band</option>
              <option value="Yellow">Yellow</option><option value="Red">Red</option><option value="Green">Green</option>
              <option value="Blue">Blue</option><option value="Black">Black</option><option value="Purple">Purple</option><option value="Orange">Orange</option>
            </select>
          ) : (
            <input className="set-input" type="number" placeholder="Weight" value={set.weight || ''}
              onChange={e => onChange({ ...set, weight: +e.target.value || null })} />
          )}
        </>
      )}
      <button className="btn-icon btn-delete" onClick={onDelete}>&times;</button>
    </div>
  );
}

// ==================== Exercise Card ====================
function ExerciseCard({ exercise, onUpdate, onPickExercise, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, index, onDragStart, onDragOver, onDrop, circuitPosition, circuitTotal, sectionNames, currentSection, onMoveToSection }) {
  const isBand = (exercise.weight_type || '').startsWith('Bands');
  const isCircuit = exercise.set_type === 'Circuit';
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  function addSet() {
    const lastSet = exercise.sets[exercise.sets.length - 1] || {};
    onUpdate({ ...exercise, sets: [...exercise.sets, { reps: lastSet.reps, weight: lastSet.weight, band_color: lastSet.band_color }] });
  }

  function updateSet(idx, newSet) {
    const sets = [...exercise.sets];
    sets[idx] = newSet;
    onUpdate({ ...exercise, sets });
  }

  function deleteSet(idx) {
    onUpdate({ ...exercise, sets: exercise.sets.filter((_, i) => i !== idx) });
  }

  return (
    <div
      className={`exercise-card ${exercise.superset_group && !isCircuit ? 'superset-card' : ''} ${isCircuit ? 'circuit-card' : ''}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', index); onDragStart && onDragStart(index); }}
      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
      onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over'); }}
      onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('drag-over'); const from = parseInt(e.dataTransfer.getData('text/plain')); onDrop && onDrop(from, index); }}
    >
      <div className="exercise-header">
        {isCircuit && circuitPosition != null && (
          <div className="circuit-num">{circuitPosition}</div>
        )}
        {!isCircuit && <div className="drag-handle" title="Drag to reorder">&#x2630;</div>}
        <div className="exercise-info">
          {exercise.exercise_id ? (
            <span className="exercise-name" onClick={() => onPickExercise && onPickExercise()} title="Click to change exercise" style={{ cursor: onPickExercise ? 'pointer' : 'default' }}>{exercise.exercise_name}</span>
          ) : (
            <button className="btn-sm btn-accent" onClick={() => onPickExercise && onPickExercise()}>+ Choose exercise</button>
          )}
          <div className="exercise-badges">
            {exercise.weight_type && <span className="badge badge-sm">{exercise.weight_type}</span>}
            {exercise.muscle_group && <span className="badge badge-sm badge-muted">{exercise.muscle_group}</span>}
            {isCircuit && <span className="badge badge-sm badge-circuit">Circuit {exercise.superset_group} • {exercise.circuit_rounds}R</span>}
            {exercise.superset_group && !isCircuit && <span className="badge badge-sm badge-accent">SS {exercise.superset_group}</span>}
          </div>
        </div>
        <div className="exercise-actions">
          {canMoveUp && <button className="btn-icon" onClick={onMoveUp} title="Move up">&#8593;</button>}
          {canMoveDown && <button className="btn-icon" onClick={onMoveDown} title="Move down">&#8595;</button>}
          {sectionNames && sectionNames.length > 1 && (
            showMoveMenu ? (
              <div style={{position:'absolute',right:0,top:'100%',zIndex:10,background:'#1a1f2e',border:'1px solid #333',borderRadius:4,padding:4,minWidth:120}}>
                {sectionNames.map((name, si) => si !== currentSection ? (
                  <button key={si} className="btn-sm" style={{display:'block',width:'100%',textAlign:'left',marginBottom:2,fontSize:11}} onClick={() => {
                    try { onMoveToSection && onMoveToSection(index, si); } catch(err) { console.error(err); window.alert('Move failed'); }
                    setShowMoveMenu(false);
                  }}>→ {name}</button>
                ) : null)}
                <button className="btn-sm" style={{fontSize:11,color:'#999',width:'100%',textAlign:'left'}} onClick={() => setShowMoveMenu(false)}>Cancel</button>
              </div>
            ) : (
              <button className="btn-sm" style={{fontSize:11}} onClick={() => setShowMoveMenu(true)} title="Move to section">Move</button>
            )
          )}
          <button className="btn-icon btn-delete" onClick={onRemove} title="Remove">&times;</button>
        </div>
      </div>
      <div className="sets-header">
        <span>Set</span>
        {exercise.weight_type === 'Cardio' ? (
          <><span>Seconds</span><span>% Effort</span><span>Weight</span></>
        ) : exercise.weight_type === 'Body Weight' ? (
          <><span>Reps</span><span>Seconds</span><span>% Effort</span><span>Weight</span></>
        ) : (
          <><span>Reps</span><span>{isBand ? 'Band' : 'Weight'}</span></>
        )}
        <span></span>
      </div>
      {exercise.sets.map((set, idx) => (
        <SetRow key={idx} set={set} setIndex={idx} isBand={isBand} isCardio={exercise.weight_type === 'Cardio'} isBodyWeight={exercise.weight_type === 'Body Weight'} onChange={s => updateSet(idx, s)} onDelete={() => deleteSet(idx)} />
      ))}
      <button className="btn-add-set" onClick={addSet}>+ Add Set</button>
      <div className="rest-time-row">
        <label>Rest:</label>
        <input type="number" className="rest-input" placeholder="sec" value={exercise.rest_seconds || ''}
          onChange={e => onUpdate({ ...exercise, rest_seconds: +e.target.value || null })} />
        <span className="rest-label">seconds</span>
      </div>
    </div>
  );
}

// ==================== Section ====================
function Section({ section, sectionIndex, onUpdate, onRemove, onAddExercise, onAddBlock, onPickExercise, onMoveUp, onMoveDown, canMoveUp, canMoveDown, sectionNames, onMoveExerciseToSection }) {
  function updateExercise(exIdx, updated) {
    const exercises = [...section.exercises];
    exercises[exIdx] = updated;
    onUpdate({ ...section, exercises });
  }

  function removeExercise(exIdx) {
    onUpdate({ ...section, exercises: section.exercises.filter((_, i) => i !== exIdx) });
  }

  function moveExercise(exIdx, dir) {
    const exercises = [...section.exercises];
    const newIdx = exIdx + dir;
    if (newIdx < 0 || newIdx >= exercises.length) return;
    [exercises[exIdx], exercises[newIdx]] = [exercises[newIdx], exercises[exIdx]];
    onUpdate({ ...section, exercises });
  }

  return (
    <div className="section">
      <div className="section-header">
        <div className="section-title-row">
          <select className="section-select" value={section.name} onChange={e => onUpdate({ ...section, name: e.target.value })}>
            <option value="Warmup">Warmup</option>
            <option value="Main Workout">Main Workout</option>
            <option value="Finisher">Finisher</option>
            <option value="Cool-down">Cool-down</option>
            <option value="Mobility">Mobility</option>
          </select>
          <span className="section-count">{section.exercises.length} exercises</span>
        </div>
        <div className="section-actions">
          {canMoveUp && <button className="btn-icon" onClick={onMoveUp} title="Move section up">&#8593;</button>}
          {canMoveDown && <button className="btn-icon" onClick={onMoveDown} title="Move section down">&#8595;</button>}
          <button className="btn-sm" onClick={onAddExercise}>+ Exercise</button>
          <button className="btn-sm btn-accent" onClick={onAddBlock}>+ Block</button>
          <button className="btn-icon btn-delete" onClick={onRemove}>&times;</button>
        </div>
      </div>
      <div className="section-body">
        {section.exercises.length === 0 && (
          <div className="empty-section">No exercises yet. Click + Exercise or + Block to add.</div>
        )}
        {(() => {
          // Build display items: circuit round headers + exercise cards
          const items = [];
          const seenCircuitRounds = new Set();
          let globalRoundCounter = 0;

          // Count total rounds across all circuits for display
          const circuitGroups = {};
          section.exercises.forEach(ex => {
            if (ex.set_type === 'Circuit' && ex.superset_group) {
              if (!circuitGroups[ex.superset_group]) circuitGroups[ex.superset_group] = { exercises: [], rounds: ex.circuit_rounds || 2 };
              circuitGroups[ex.superset_group].exercises.push(ex);
            }
          });

          section.exercises.forEach((ex, idx) => {
            // Insert circuit round header before first exercise of each circuit
            if (ex.set_type === 'Circuit' && ex.superset_group) {
              const circuitKey = ex.superset_group;
              if (!seenCircuitRounds.has(circuitKey)) {
                seenCircuitRounds.add(circuitKey);
                const cg = circuitGroups[circuitKey];
                const totalCircuitRounds = cg.rounds;
                // Show headers for each round
                for (let r = 1; r <= totalCircuitRounds; r++) {
                  globalRoundCounter++;
                  if (r === 1) {
                    // Only insert the first round header here — exercises follow
                    items.push(
                      <div key={`circuit-header-${circuitKey}`} className="circuit-round-header">
                        <div className="circuit-round-badge">{globalRoundCounter}</div>
                        <span>Circuit {circuitKey} — Round {r} of {totalCircuitRounds}</span>
                        <span className="circuit-exercise-count">{cg.exercises.length} exercises</span>
                      </div>
                    );
                  }
                }
              }
            }

            items.push(
              <ExerciseCard
                key={ex._key}
                exercise={ex}
                index={idx}
                circuitPosition={null}
                circuitTotal={null}
                onUpdate={updated => updateExercise(idx, updated)}
                onPickExercise={() => onPickExercise && onPickExercise(idx)}
                onRemove={() => removeExercise(idx)}
                onMoveUp={() => moveExercise(idx, -1)}
                onMoveDown={() => moveExercise(idx, 1)}
                canMoveUp={idx > 0}
                canMoveDown={idx < section.exercises.length - 1}
                sectionNames={sectionNames}
                currentSection={sectionIndex}
                onMoveToSection={(exIdx, targetSectionIdx) => onMoveExerciseToSection && onMoveExerciseToSection(sectionIndex, exIdx, targetSectionIdx)}
                onDragStart={() => {}}
                onDrop={(from, to) => {
                  if (from === to) return;
                  const exercises = [...section.exercises];
                  const [moved] = exercises.splice(from, 1);
                  exercises.splice(to, 0, moved);
                  onUpdate({ ...section, exercises });
                }}
              />
            );

            // After last exercise of a circuit, show remaining round headers
            if (ex.set_type === 'Circuit' && ex.superset_group) {
              const cg = circuitGroups[ex.superset_group];
              const isLast = cg.exercises[cg.exercises.length - 1] === ex;
              if (isLast && cg.rounds > 1) {
                for (let r = 2; r <= cg.rounds; r++) {
                  items.push(
                    <div key={`circuit-repeat-${ex.superset_group}-r${r}`} className="circuit-repeat-header">
                      <span>↻ Repeat Circuit {ex.superset_group} — Round {r} of {cg.rounds}</span>
                    </div>
                  );
                }
              }
            }
          });

          return items;
        })()}
      </div>
    </div>
  );
}

// ==================== Auth Gate (login/signup toggle) ====================
function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch {
      setError('Could not send reset email. Check the address and try again.');
    } finally { setLoading(false); }
  }

  return (
    <div className="ls-shell">
      <div className="ls-left">
        <div className="ls-orb ls-orb-a" /><div className="ls-orb ls-orb-b" />
        <div className="ls-logo">
          <div className="ls-logo-mark">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="2" y1="12" x2="22" y2="12"/>
              <rect x="1" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="20.5" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="3.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
              <rect x="18.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
            </svg>
          </div>
          <span className="ls-logo-name">FitMagic</span>
        </div>
        <div className="ls-headline">
          <h1 className="ls-h1">Reset your<br/><span className="ls-h1-accent">password.</span></h1>
          <p className="ls-tagline">We'll send a link to your email address.</p>
        </div>
      </div>
      <div className="ls-right">
        <div className="ls-form-wrap">
          {sent ? (
            <>
              <div className="ls-eyebrow">Email sent</div>
              <h2 className="ls-form-heading">Check your<br/>inbox</h2>
              <p className="ls-form-sub" style={{ marginBottom: 32 }}>
                We sent a reset link to <strong style={{ color: '#f1f5f9' }}>{email}</strong>. It expires in 1 hour.
              </p>
              <button onClick={onBack} className="ls-btn-submit">Back to Sign In</button>
            </>
          ) : (
            <>
              <div className="ls-eyebrow">Account recovery</div>
              <h2 className="ls-form-heading">Forgot your<br/>password?</h2>
              <p className="ls-form-sub">Enter your email and we'll send a reset link.</p>
              <form onSubmit={handleSubmit}>
                <div className="ls-field">
                  <label className="ls-label">Email</label>
                  <input className="ls-input" type="email" required autoFocus
                    value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                {error && <div className="ls-error">{error}</div>}
                <button className="ls-btn-submit" type="submit" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
              <p style={{ marginTop: 16, fontSize: 13, color: '#64748b', textAlign: 'center' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 13 }}>
                  ← Back to Sign In
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetPasswordScreen({ token, onSuccess, onBack }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Reset failed. The link may have expired.');
    } finally { setLoading(false); }
  }

  return (
    <div className="ls-shell">
      <div className="ls-left">
        <div className="ls-orb ls-orb-a" /><div className="ls-orb ls-orb-b" />
        <div className="ls-logo">
          <div className="ls-logo-mark">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="2" y1="12" x2="22" y2="12"/>
              <rect x="1" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="20.5" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="3.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
              <rect x="18.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
            </svg>
          </div>
          <span className="ls-logo-name">FitMagic</span>
        </div>
        <div className="ls-headline">
          <h1 className="ls-h1">New<br/><span className="ls-h1-accent">password.</span></h1>
          <p className="ls-tagline">Choose something you'll remember.</p>
        </div>
      </div>
      <div className="ls-right">
        <div className="ls-form-wrap">
          {done ? (
            <>
              <div className="ls-eyebrow">All set</div>
              <h2 className="ls-form-heading">Password<br/>updated</h2>
              <p className="ls-form-sub" style={{ marginBottom: 32 }}>Your password has been changed. Sign in with your new password.</p>
              <button onClick={onBack} className="ls-btn-submit">Sign In</button>
            </>
          ) : (
            <>
              <div className="ls-eyebrow">Account recovery</div>
              <h2 className="ls-form-heading">Set a new<br/>password</h2>
              <p className="ls-form-sub">Must be at least 6 characters.</p>
              <form onSubmit={handleSubmit}>
                <div className="ls-field">
                  <label className="ls-label">New Password</label>
                  <input className="ls-input" type="password" required minLength={6} autoFocus
                    value={password} onChange={e => setPassword(e.target.value)} />
                </div>
                <div className="ls-field">
                  <label className="ls-label">Confirm Password</label>
                  <input className="ls-input" type="password" required minLength={6}
                    value={confirm} onChange={e => setConfirm(e.target.value)} />
                </div>
                {error && <div className="ls-error">{error}</div>}
                <button className="ls-btn-submit" type="submit" disabled={loading}>
                  {loading ? 'Updating…' : 'Update Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AuthGate({ onLogin, defaultMode = 'login' }) {
  const resetToken = new URLSearchParams(window.location.search).get('reset_token');
  const [mode, setMode] = useState(resetToken ? 'reset' : defaultMode);
  if (mode === 'signup') return <SignupScreen onLogin={onLogin} onSwitchToLogin={() => setMode('login')} />;
  if (mode === 'forgot') return <ForgotPasswordScreen onBack={() => setMode('login')} />;
  if (mode === 'reset') return <ResetPasswordScreen token={resetToken} onSuccess={onLogin} onBack={() => setMode('login')} />;
  return <LoginScreen onLogin={onLogin} onSwitchToSignup={() => setMode('signup')} onForgotPassword={() => setMode('forgot')} />;
}

// Whole-year age from a YYYY-MM-DD date string; returns NaN for an invalid date.
function ageFromDob(dobStr) {
  if (!dobStr) return NaN;
  const dob = new Date(`${dobStr}T00:00:00`);
  if (isNaN(dob.getTime())) return NaN;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function SignupScreen({ onLogin, onSwitchToLogin }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showReferral, setShowReferral] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const todayStr = new Date().toISOString().slice(0, 10);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!firstName.trim()) { setError('First name is required'); return; }
    if (!dob) { setError('Date of birth is required'); return; }
    const age = ageFromDob(dob);
    if (isNaN(age) || age < 0 || age > 120) { setError('Please enter a valid date of birth'); return; }
    // COPPA hard gate: under-13 cannot create an account (no account, no data stored).
    if (age < 13) { setError('You must be 13 or older to use FitMagic.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.register(email.trim(), password, firstName.trim(), lastName.trim(), referralCode.trim(), dob);
      onLogin();
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ls-shell">
      <div className="ls-left">
        <div className="ls-orb ls-orb-a" />
        <div className="ls-orb ls-orb-b" />
        <div className="ls-logo">
          <div className="ls-logo-mark">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="2" y1="12" x2="22" y2="12"/>
              <rect x="1" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="20.5" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="3.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
              <rect x="18.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
            </svg>
          </div>
          <span className="ls-logo-name">FitMagic</span>
        </div>
        <div className="ls-headline">
          <h1 className="ls-h1">Your coach<br/><span className="ls-h1-accent">knows</span><br/>everything.</h1>
          <p className="ls-tagline">Workouts, nutrition, supplements, and health data — all in one place, coached by AI.</p>
        </div>
        <div className="ls-tags">
          {['AI Coach', 'Workout Tracking', 'Nutrition Logging', 'Supplements & Peptides', 'Health Data'].map(t => (
            <span key={t} className="ls-tag">{t}</span>
          ))}
        </div>
      </div>
      <div className="ls-right">
        <div className="ls-form-wrap">
          <div className="ls-eyebrow">Get Started</div>
          <h2 className="ls-form-heading">Create your<br/>account</h2>
          <p className="ls-form-sub">Free to try. No credit card required.</p>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="ls-field" style={{ flex: 1 }}>
                <label className="ls-label">First Name</label>
                <input className="ls-input" type="text" required autoFocus
                  value={firstName} onChange={e => setFirstName(e.target.value)} />
              </div>
              <div className="ls-field" style={{ flex: 1 }}>
                <label className="ls-label">Last Name</label>
                <input className="ls-input" type="text"
                  value={lastName} onChange={e => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="ls-field">
              <label className="ls-label">Date of Birth</label>
              <input className="ls-input" type="date" required
                max={todayStr} value={dob} onChange={e => setDob(e.target.value)} />
            </div>
            <div className="ls-field">
              <label className="ls-label">Email</label>
              <input className="ls-input" type="email" required
                value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="ls-field">
              <label className="ls-label">Password</label>
              <input className="ls-input" type="password" required minLength={6}
                value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            {showReferral ? (
              <div className="ls-field">
                <label className="ls-label">Referral Code</label>
                <input className="ls-input" type="text" autoFocus placeholder="e.g. FOUNDERS"
                  value={referralCode} onChange={e => setReferralCode(e.target.value)} />
              </div>
            ) : (
              <p className="ls-switch" style={{ marginBottom: 18, textAlign: 'left' }}>
                <button type="button" onClick={() => setShowReferral(true)}>Have a referral code?</button>
              </p>
            )}
            {error && <div className="ls-error">{error}</div>}
            <button className="ls-btn-submit" type="submit" disabled={loading}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
          <p className="ls-switch">
            Already have an account?{' '}
            <button onClick={onSwitchToLogin}>Sign in</button>
          </p>
          <p className="ls-switch" style={{ marginTop: 10, fontSize: 12 }}>
            By creating an account you agree to our{' '}
            <a href="/terms.html">Terms</a> and{' '}
            <a href="/privacy.html">Privacy Policy</a>.
          </p>
          <p className="ls-switch" style={{ marginTop: 16 }}>
            <a href="/landing.html" style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>← Back to home</a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== Login Screen ====================
function LoginScreen({ onLogin, onSwitchToSignup, onForgotPassword }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.login(email.trim(), password);
      onLogin();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }


  return (
    <div className="ls-shell">
      {/* Left — brand */}
      <div className="ls-left">
        <div className="ls-orb ls-orb-a" />
        <div className="ls-orb ls-orb-b" />

        {/* Logo */}
        <div className="ls-logo">
          <div className="ls-logo-mark">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="2" y1="12" x2="22" y2="12"/>
              <rect x="1" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="20.5" y="9.5" width="2.5" height="5" rx="1" fill="white" stroke="none"/>
              <rect x="3.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
              <rect x="18.5" y="7.5" width="2" height="9" rx="1" fill="white" stroke="none"/>
            </svg>
          </div>
          <span className="ls-logo-name">FitMagic</span>
        </div>

        {/* Headline */}
        <div className="ls-headline">
          <h1 className="ls-h1">
            Your coach<br/>
            <span className="ls-h1-accent">knows</span><br/>
            everything.
          </h1>
          <p className="ls-tagline">Workouts, nutrition, supplements, and health data — all in one place, coached by AI.</p>
        </div>

        {/* Feature tags */}
        <div className="ls-tags">
          {['AI Coach', 'Workout Tracking', 'Nutrition Logging', 'Supplements & Peptides', 'Health Data'].map(t => (
            <span key={t} className="ls-tag">{t}</span>
          ))}
        </div>
      </div>

      {/* Right — form */}
      <div className="ls-right">
        <div className="ls-form-wrap">
          <div className="ls-eyebrow">Welcome back</div>
          <h2 className="ls-form-heading">Sign in to<br/>your account</h2>
          <p className="ls-form-sub">Enter your credentials below.</p>
          <form onSubmit={handleSubmit}>
            <div className="ls-field">
              <label className="ls-label">Email</label>
              <input className="ls-input" type="email" required autoFocus
                value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="ls-field">
              <label className="ls-label">Password</label>
              <input className="ls-input" type="password" required
                value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            {error && <div className="ls-error">{error}</div>}
            <button className="ls-btn-submit" type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
          {onForgotPassword && (
            <p className="ls-switch">
              <button onClick={onForgotPassword}>Forgot password?</button>
            </p>
          )}
          {onSwitchToSignup && (
            <p className="ls-switch">
              Don't have an account?{' '}
              <button onClick={onSwitchToSignup}>Sign up</button>
            </p>
          )}
          <p className="ls-switch" style={{ marginTop: 16 }}>
            <a href="/landing.html" style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>← Back to home</a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== Main App ====================
function App() {
  const [authStatus, setAuthStatus] = useState('loading'); // 'loading' | 'authed' | 'login-required'
  const [profileId, setProfileId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userFirstName, setUserFirstName] = useState(''); // greeting only — from /auth/me, never persisted
  const [view, setView] = useState('builder'); // 'builder' | 'analytics' | 'coaching'
  const [programs, setPrograms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Template builder state
  const [templateName, setTemplateName] = useState('');
  const [workoutType, setWorkoutType] = useState('Weight Training');
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Modals
  const [showExerciseSearch, setShowExerciseSearch] = useState(null); // null | { sectionIdx, exIdx } — exIdx null = append new, else fill that slot
  const [showBlockCreator, setShowBlockCreator] = useState(null); // sectionIndex
  const [editingProgram, setEditingProgram] = useState(null); // program id being edited
  const [deletingProgram, setDeletingProgram] = useState(null); // program id awaiting delete confirmation

  // Key counter for unique React keys
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [keyCounter, setKeyCounter] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const nextKey = useCallback(() => { setKeyCounter(c => c + 1); return `k${keyCounter}`; }, [keyCounter]);

  function resolveProfile() {
    api.getMe().then(me => {
      const pid = me.fitness_profile_id;
      if (!pid) { setAuthStatus('login-required'); return; }
      setProfileId(pid);
      setIsAdmin(!!me.is_admin);
      // Bug #202: greet the signed-in user. Blank (or email-looking) → no name at all.
      const fn = typeof me.first_name === 'string' ? me.first_name.trim() : '';
      setUserFirstName(fn && !fn.includes('@') ? fn : '');
      setAuthStatus('authed');
      api.getPrograms(pid).then(setPrograms).catch(console.error);
      api.getTemplates(pid).then(setTemplates).catch(console.error);
    }).catch(() => { api.clearSessionToken(); setUserFirstName(''); setAuthStatus('login-required'); });
  }

  useEffect(() => { resolveProfile(); }, []);

  useEffect(() => {
    if (!profileId) return;
    try {
      const tid = new URLSearchParams(window.location.search).get('template');
      if (tid) loadTemplate(parseInt(tid, 10));
    } catch {} // eslint-disable-line no-empty
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTemplate(tid) {
    try {
      const data = await api.getTemplateDetail(profileId, tid);
      setSelectedTemplate(data.template);
      setTemplateName(data.template.name || '');
      setWorkoutType(data.template.workout_type || 'Weight Training');
      if (data.template.program_id) setSelectedProgram(data.template.program_id);

      // Group sets by workout_group into sections
      const sectionMap = {};
      const sectionOrder = [];
      for (const s of data.sets) {
        const name = s.workout_group || 'Main Workout';
        if (!sectionMap[name]) { sectionMap[name] = []; sectionOrder.push(name); }
        sectionMap[name].push(s);
      }

      // Build sections with exercises
      // For circuits/supersets: group by superset_group, take round 1 as the exercise list,
      //   use circuit_rounds as the number of sets per exercise
      // For straight sets: group by exercise_id, each set is one set
      const builtSections = sectionOrder.map(name => {
        const sets = sectionMap[name];
        const exercises = [];
        const seenCircuitGroups = new Set();

        for (const s of sets) {
          if (s.superset_group && (s.set_type === 'Circuit' || s.set_type === 'Superset')) {
            const circuitKey = s.superset_group;
            if (seenCircuitGroups.has(circuitKey)) continue;

            // Get round 1 sets for this circuit — defines exercise order and positions
            const round1Sets = sets.filter(cs => cs.superset_group === circuitKey && cs.round_number === 1);
            const rounds = s.circuit_rounds || 2;

            // Each position in round 1 becomes one exercise card with 1 set
            // Rounds are handled by the "repeat" display, not by adding more set rows
            for (let pos = 0; pos < round1Sets.length; pos++) {
              const cs = round1Sets[pos];
              // Collect sets for all rounds at this exercise position (keyed by exercise_id within the group)
              const allRoundsForPos = sets
                .filter(rs => rs.superset_group === circuitKey && rs.exercise_id === cs.exercise_id)
                .sort((a, b) => (a.round_number || 0) - (b.round_number || 0));
              exercises.push({
                _key: `ss_${circuitKey}_pos${pos}_${cs.set_num}`,
                exercise_id: cs.exercise_id,
                exercise_name: cs.exercise_name,
                weight_type: cs.weight_type,
                muscle_group: cs.muscle_group,
                set_type: s.set_type || 'Circuit',
                superset_group: circuitKey,
                circuit_rounds: rounds,
                rest_seconds: cs.rest_seconds || null,
                sets: allRoundsForPos.map(rs => ({
                  reps: rs.reps, weight: rs.weight, band_color: rs.band_color, time: rs.time,
                  effort_pct: rs.effort_pct || null, duration_seconds: rs.duration_seconds || null,
                })),
              });
            }
            seenCircuitGroups.add(circuitKey);
          } else {
            // Straight set — group by exercise_id
            const existing = exercises.find(e => !e.superset_group && e.exercise_id === s.exercise_id);
            if (existing) {
              existing.sets.push({ reps: s.reps, weight: s.weight, band_color: s.band_color, time: s.time, effort_pct: s.effort_pct || null, duration_seconds: s.duration_seconds || null });
            } else {
              exercises.push({
                _key: `ex_${s.exercise_id}_${s.set_num}`,
                exercise_id: s.exercise_id,
                exercise_name: s.exercise_name,
                weight_type: s.weight_type,
                muscle_group: s.muscle_group,
                set_type: 'Straight Set',
                superset_group: null,
                circuit_rounds: null,
                rest_seconds: s.rest_seconds || null,
                sets: [{ reps: s.reps, weight: s.weight, band_color: s.band_color, time: s.time, effort_pct: s.effort_pct || null, duration_seconds: s.duration_seconds || null }],
              });
            }
          }
        }

        return { name, exercises };
      });

      setSections(builtSections);
    } catch (e) {
      console.error(e);
      alert('Failed to load template: ' + e.message);
    }
  }

  function addSection() {
    setSections([...sections, { name: 'Main Workout', exercises: [] }]);
  }

  function updateSection(idx, updated) {
    const s = [...sections];
    s[idx] = updated;
    setSections(s);
  }

  function removeSection(idx) {
    if (!window.confirm('Remove this section and all its exercises?')) return;
    setSections(sections.filter((_, i) => i !== idx));
  }

  function moveSection(idx, dir) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const s = [...sections];
    [s[idx], s[newIdx]] = [s[newIdx], s[idx]];
    setSections(s);
  }

  function handleAddExercise(sectionIdx, exercise) {
    const s = [...sections];
    s[sectionIdx].exercises.push({
      _key: `new_${Date.now()}_${Math.random()}`,
      exercise_id: exercise.id,
      exercise_name: exercise.exercise_name,
      weight_type: exercise.weight_type,
      muscle_group: exercise.muscle_group,
      set_type: 'Straight Set',
      superset_group: null,
      sets: [{ reps: 10, weight: null }, { reps: 10, weight: null }, { reps: 10, weight: null }],
    });
    setSections(s);
    setShowExerciseSearch(null);
  }

  // Assign an exercise to an existing card (circuit/superset slot or replace).
  // Preserves the slot's sets, set_type, superset_group, and circuit_rounds.
  function handleFillSlot(sectionIdx, exIdx, exercise) {
    const s = [...sections];
    const ex = s[sectionIdx]?.exercises[exIdx];
    if (!ex) { setShowExerciseSearch(null); return; }
    s[sectionIdx].exercises[exIdx] = {
      ...ex,
      exercise_id: exercise.id,
      exercise_name: exercise.exercise_name,
      weight_type: exercise.weight_type,
      muscle_group: exercise.muscle_group ?? ex.muscle_group ?? null,
    };
    setSections(s);
    setShowExerciseSearch(null);
  }

  function handleCreateBlock(sectionIdx, exerciseCount, rounds, blockType) {
    const s = [...sections];
    // Find next superset group number
    let maxSS = 0;
    for (const sec of s) {
      for (const ex of sec.exercises) {
        if (ex.superset_group && +ex.superset_group > maxSS) maxSS = +ex.superset_group;
      }
    }
    const ssGroup = maxSS + 1;

    for (let i = 0; i < exerciseCount; i++) {
      const defaultSets = [];
      for (let r = 0; r < rounds; r++) defaultSets.push({ reps: 10, weight: null });

      s[sectionIdx].exercises.push({
        _key: `block_${Date.now()}_${i}`,
        exercise_id: null,
        exercise_name: null,
        weight_type: null,
        muscle_group: null,
        set_type: blockType,
        superset_group: String(ssGroup),
        circuit_rounds: rounds,
        sets: defaultSets,
      });
    }
    setSections(s);
  }

  // Count cards that have no exercise chosen yet (unfilled circuit/superset slots).
  function countEmptySlots() {
    let n = 0;
    sections.forEach(sec => sec.exercises.forEach(ex => { if (!ex.exercise_id) n++; }));
    return n;
  }

  function confirmEmptySlots() {
    const empty = countEmptySlots();
    if (empty === 0) return true;
    return window.confirm(
      `${empty} exercise slot${empty > 1 ? 's have' : ' has'} no exercise selected and will not be saved.\n\n` +
      `Click "Choose exercise" on each empty slot to fill it, or OK to save without them.`
    );
  }

  async function handleSave() {
    if (!selectedTemplate) { alert('Create or select a template first'); return; }
    if (!confirmEmptySlots()) return;
    setSaving(true);
    setSavedMsg('');
    try {
      // Update template metadata (name, program, workout type)
      await api.updateTemplate(selectedTemplate.id, {
        name: templateName,
        programId: selectedTemplate.program_id ?? selectedProgram,
        workoutType: workoutType,
      });
      const count = await handleSaveToTemplate(selectedTemplate.id);
      setTemplates(await api.getTemplates(profileId));
      setSavedMsg(`Saved! ${count} sets`);
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleNewTemplate() {
    const name = prompt('Template name:');
    if (!name) return;
    if (!selectedProgram) { alert('Select a program first'); return; }
    try {
      const tpl = await api.createTemplate(selectedProgram, name, workoutType);
      setSelectedTemplate(tpl);
      setTemplateName(name);
      setSections([{ name: 'Warmup', exercises: [] }, { name: 'Main Workout', exercises: [] }, { name: 'Cool-down', exercises: [] }]);
      setTemplates(await api.getTemplates(profileId));
    } catch (e) { alert(e.message); }
  }

  async function handleSaveAs() {
    const newName = prompt('Save as new template name:', templateName + ' (Copy)');
    if (!newName) return;
    if (!selectedProgram && !selectedTemplate?.program_id) { alert('Select a program first'); return; }
    if (!confirmEmptySlots()) return;
    const progId = selectedProgram || selectedTemplate?.program_id;
    try {
      setSaving(true);
      // Create new template
      const newTpl = await api.createTemplate(progId, newName, workoutType);
      // Save current sections to the new template
      await handleSaveToTemplate(newTpl.id);
      // Switch to the new template
      setSelectedTemplate({ ...newTpl, name: newName });
      setTemplateName(newName);
      setTemplates(await api.getTemplates(profileId));
      setSavedMsg(`Saved as "${newName}"!`);
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) { alert('Save As failed: ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleSaveToTemplate(templateId) {
    // Build flat set list (same logic as handleSave)
    const allSets = [];
    let setNum = 0;
    sections.forEach((section, sIdx) => {
      const groupOrder = sIdx + 1;
      const standalone = section.exercises.filter(ex => !ex.superset_group);
      const grouped = {};
      section.exercises.filter(ex => ex.superset_group).forEach(ex => {
        if (!grouped[ex.superset_group]) grouped[ex.superset_group] = [];
        grouped[ex.superset_group].push(ex);
      });
      for (const [sg, exercises] of Object.entries(grouped)) {
        const rounds = exercises[0].circuit_rounds || exercises[0].sets.length;
        for (let round = 0; round < rounds; round++) {
          for (const ex of exercises) {
            if (!ex.exercise_id) continue;
            const setData = ex.sets[round] || ex.sets[0] || {};
            setNum++;
            allSets.push({
              exercise_id: ex.exercise_id, set_num: setNum, reps: setData.reps, weight: setData.weight,
              band_color: setData.band_color || null, time: setData.time || null,
              set_type: ex.set_type || 'Superset', superset_group: sg, group_order: groupOrder,
              workout_group: section.name, number_of_sets: exercises.length,
              circuit_rounds: rounds, round_number: round + 1,
              rest_seconds: ex.rest_seconds || null, effort_pct: setData.effort_pct || null, duration_seconds: setData.duration_seconds || null,
            });
          }
        }
      }
      for (const ex of standalone) {
        if (!ex.exercise_id) continue;
        for (const set of ex.sets) {
          setNum++;
          allSets.push({
            exercise_id: ex.exercise_id, set_num: setNum, reps: set.reps, weight: set.weight,
            band_color: set.band_color || null, time: set.time || null,
            set_type: 'Straight Set', superset_group: null, group_order: groupOrder,
            workout_group: section.name, number_of_sets: ex.sets.length,
            circuit_rounds: null, round_number: null,
            rest_seconds: ex.rest_seconds || null, effort_pct: set.effort_pct || null, duration_seconds: set.duration_seconds || null,
          });
        }
      }
    });
    await api.saveTemplateSets(templateId, allSets);
    return allSets.length;
  }

  async function handleNewProgram() {
    if (!profileId) { alert('Session not ready — please refresh the page.'); return; }
    const name = prompt('Program name:');
    if (!name) return;
    try {
      const prog = await api.createProgram(profileId, name, '', 0);
      setPrograms(await api.getPrograms(profileId));
      setSelectedProgram(prog.id);
    } catch (e) { alert(e.message); }
  }

  if (authStatus === 'loading') {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#0d1117', color: '#8b949e', fontSize: 14 }}>Loading…</div>;
  }

  if (authStatus === 'login-required') {
    const startOnSignup = window.location.hash === '#signup';
    return <AuthGate onLogin={resolveProfile} defaultMode={startOnSignup ? 'signup' : 'login'} />;
  }

  const prog = selectedProgram ? programs.find(p => p.id === selectedProgram) : null;
  const progTemplates = prog ? templates.filter(t => t.program_id === prog.id) : [];

  return (
    <div className="app">
      <header className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => setSidebarOpen(o => !o)}
            title={sidebarOpen ? 'Hide panel' : 'Show panel'}
            style={{ background: 'none', border: '1px solid #334155', borderRadius: 6, color: '#94a3b8', cursor: 'pointer', padding: '5px 8px', fontSize: 16, lineHeight: 1 }}
          >{sidebarOpen ? '◀' : '▶'}</button>
          <h1 style={{ margin: 0 }}>FitMagic</h1>
          <nav style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setView('builder')}
              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: view === 'builder' ? '#f97316' : 'transparent', color: view === 'builder' ? '#fff' : '#64748b' }}
            >Builder</button>
            <button
              onClick={() => setView('analytics')}
              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: view === 'analytics' ? '#f97316' : 'transparent', color: view === 'analytics' ? '#fff' : '#64748b' }}
            >Analytics</button>
            <button
              onClick={() => setView('coaching')}
              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: view === 'coaching' ? '#C3F53B' : 'transparent', color: view === 'coaching' ? '#0B0C09' : '#64748b' }}
            >Coaching</button>
          </nav>
        </div>
        <div className="header-actions">
          {view === 'builder' && selectedTemplate && (
            <>
              <button className="btn-cancel" onClick={() => {
                if (window.confirm('Discard unsaved changes?')) {
                  setSelectedTemplate(null);
                  setSections([]);
                  setTemplateName('');
                }
              }}>
                Cancel
              </button>
              <button className="btn-secondary" onClick={handleSaveAs} disabled={saving}>
                Save As...
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Template'}
              </button>
              {savedMsg && <span className="saved-msg">{savedMsg}</span>}
            </>
          )}
        </div>
      </header>

      {view === 'analytics' && <AnalyticsView profileId={profileId}/>}

      {/* No profileId prop by design (TDD_107a): caller identity is the JWT,
          server-side, exclusively. 401 handling lives here because authStatus
          is App-private state — clearing storage inside CoachView re-renders
          nothing, and api.js owns no UI state. */}
      {view === 'coaching' && (
        <CoachView
          onUnauthorized={() => { api.clearSessionToken(); setUserFirstName(''); setAuthStatus('login-required'); }}
          onAdjustPlan={() => setView('builder')}
        />
      )}

      <div className="layout" style={{ display: view === 'builder' ? undefined : 'none' }}>
        {/* Sidebar */}
        <aside className="sidebar" style={{ display: sidebarOpen ? undefined : 'none' }}>
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <h3>Program</h3>
              <button className="btn-sm" onClick={handleNewProgram}>+ New</button>
            </div>
            <select className="full-width" value={selectedProgram || ''} onChange={e => { setSelectedProgram(+e.target.value); }}>
              <option value="">All programs</option>
              {programs.map(p => <option key={p.id} value={p.id}>{p.program_name}</option>)}
            </select>

            {/* Program Detail Card */}
            {prog && (
                <div className="program-card">
                  <div className="program-card-header">
                    <span className="program-card-name">{prog.program_name}</span>
                    <button className="btn-icon" onClick={() => setEditingProgram(editingProgram === prog.id ? null : prog.id)} title="Edit">
                      {editingProgram === prog.id ? '▲' : '✎'}
                    </button>
                  </div>

                  {editingProgram === prog.id ? (
                    <div className="program-edit-form">
                      <div className="form-group">
                        <label>Program Name</label>
                        <input type="text" defaultValue={prog.program_name}
                          onChange={e => { prog._editName = e.target.value; }} />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea defaultValue={prog.description || ''} placeholder="Program description..."
                          rows={3} onChange={e => { prog._editDesc = e.target.value; }} />
                      </div>
                      <div className="form-group">
                        <label>Split Type</label>
                        <select defaultValue={prog.split_type || ''} onChange={e => { prog._editSplit = e.target.value; }}>
                          <option value="">None</option>
                          <option value="Push/Pull/Legs">Push/Pull/Legs</option>
                          <option value="Upper/Lower">Upper/Lower</option>
                          <option value="Full Body">Full Body</option>
                          <option value="Body Part Split">Body Part Split</option>
                          <option value="Push/Pull">Push/Pull</option>
                          <option value="Chest/Back, Shoulders/Arms, Legs">Chest/Back, Shoulders/Arms, Legs</option>
                          <option value="Cardio/Strength">Cardio/Strength</option>
                          <option value="Circuit Training">Circuit Training</option>
                          <option value="Custom">Custom</option>
                        </select>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Days/Week</label>
                          <input type="number" min={1} max={7} defaultValue={prog.days_per_week || ''}
                            onChange={e => { prog._editDays = +e.target.value; }} />
                        </div>
                        <div className="form-group">
                          <label>Goal</label>
                          <select defaultValue={prog.goal || ''} onChange={e => { prog._editGoal = e.target.value; }}>
                            <option value="">None</option>
                            <option value="Fat Loss">Fat Loss</option>
                            <option value="Muscle Gain">Muscle Gain</option>
                            <option value="Strength">Strength</option>
                            <option value="Body Recomposition">Body Recomposition</option>
                            <option value="Stamina">Stamina</option>
                            <option value="General Health">General Health</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Parent Program</label>
                        <select defaultValue={prog.parent_program_id || ''} onChange={e => { prog._editParent = +e.target.value || null; }}>
                          <option value="">None</option>
                          {programs.filter(p => p.id !== prog.id).map(p => (
                            <option key={p.id} value={p.id}>{p.program_name}</option>
                          ))}
                        </select>
                      </div>
                      {prog.ai_generated && prog.generation_notes && (
                        <div className="form-group">
                          <label>AI Generation Notes</label>
                          <div className="ai-notes">{prog.generation_notes}</div>
                        </div>
                      )}
                      <div className="program-edit-actions">
                        <button className="btn-sm" onClick={async () => {
                          try {
                            await api.updateProgram(prog.id, {
                              name: prog._editName || prog.program_name,
                              description: prog._editDesc !== undefined ? prog._editDesc : prog.description,
                              splitType: prog._editSplit !== undefined ? prog._editSplit : prog.split_type,
                              daysPerWeek: prog._editDays !== undefined ? prog._editDays : prog.days_per_week,
                              goal: prog._editGoal !== undefined ? prog._editGoal : prog.goal,
                              parentProgramId: prog._editParent !== undefined ? prog._editParent : prog.parent_program_id,
                            });
                            setPrograms(await api.getPrograms(profileId));
                            setEditingProgram(null);
                          } catch (e) { alert('Update failed: ' + e.message); }
                        }}>Save Program</button>
                        {deletingProgram === prog.id ? (
                          <div className="program-delete-confirm">
                            <span className="program-delete-prompt">Delete "{prog.program_name}" and all templates?</span>
                            <button className="btn-sm btn-delete" onClick={async () => {
                              try {
                                await api.deleteProgram(prog.id);
                                setPrograms(await api.getPrograms(profileId));
                                setTemplates(await api.getTemplates(profileId));
                                setSelectedProgram(null);
                                setEditingProgram(null);
                                setDeletingProgram(null);
                              } catch (e) { alert('Delete failed: ' + e.message); }
                            }}>Yes</button>
                            <button className="btn-sm" onClick={async () => {
                              try {
                                await api.deleteProgram(prog.id, { keepTemplates: true });
                                setPrograms(await api.getPrograms(profileId));
                                setTemplates(await api.getTemplates(profileId));
                                setSelectedProgram(null);
                                setEditingProgram(null);
                                setDeletingProgram(null);
                              } catch (e) { alert('Delete failed: ' + e.message); }
                            }}>Delete Program - Keep Templates</button>
                            <button className="btn-sm" onClick={() => setDeletingProgram(null)}>No</button>
                          </div>
                        ) : (
                          <button className="btn-icon btn-delete" onClick={() => setDeletingProgram(prog.id)} title="Delete program">🗑</button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="program-card-details">
                      {prog.description && <div className="program-description">{prog.description}</div>}
                      {prog.split_type && <div className="program-detail-row"><span className="detail-label">Split</span><span>{prog.split_type}</span></div>}
                      {prog.days_per_week && <div className="program-detail-row"><span className="detail-label">Days/Week</span><span>{prog.days_per_week}</span></div>}
                      {prog.goal && <div className="program-detail-row"><span className="detail-label">Goal</span><span>{prog.goal}</span></div>}
                      {prog.parent_program_id && <div className="program-detail-row"><span className="detail-label">Parent</span><span>{programs.find(p => p.id === prog.parent_program_id)?.program_name || '—'}</span></div>}
                      <div className="program-detail-row"><span className="detail-label">Templates</span><span>{progTemplates.length}</span></div>
                      {prog.ai_generated && <div className="program-detail-row"><span className="badge badge-sm badge-accent">AI Generated</span></div>}
                    </div>
                  )}
                </div>
            )}
          </div>

          {isAdmin && (
            <div className="sidebar-section sidebar-section-admin">
              <div className="sidebar-section-header">
                <h3>Admin</h3>
              </div>
              <button
                className="btn-sm btn-admin-tool"
                onClick={() => {
                  const adminWin = window.open(`${api.ORIGIN}/dev/template-admin`, '_blank');
                  const sendToken = (e) => {
                    if (e.source === adminWin && e.data === 'fitmagic:ready') {
                      adminWin.postMessage(
                        { type: 'fitmagic:token', token: sessionStorage.getItem('fitmagic_builder_token') },
                        api.ORIGIN
                      );
                      window.removeEventListener('message', sendToken);
                    }
                  };
                  window.addEventListener('message', sendToken);
                }}
              >
                Template Manager ↗
              </button>
            </div>
          )}

          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <h3>Templates</h3>
              <button className="btn-sm" onClick={handleNewTemplate}>+ New</button>
            </div>
            <div className="template-list">
              {(selectedProgram ? templates.filter(t => t.program_id === selectedProgram) : templates).map(t => (
                <div
                  key={t.id}
                  className={`template-item ${selectedTemplate?.id === t.id ? 'active' : ''}`}
                  onClick={() => loadTemplate(t.id)}
                >
                  <div className="template-item-info">
                    <span className="template-name">{t.name}</span>
                    {!selectedProgram && <span className="template-program">{t.program_name}</span>}
                  </div>
                  <span className="template-meta">{t.set_count || 0} sets</span>
                </div>
              ))}
              {templates.length === 0 && <div className="empty">No templates yet</div>}
            </div>
          </div>
        </aside>

        {/* Main Builder */}
        <main className="builder">
          {selectedTemplate ? (
            <>
              <div className="builder-header">
                <input
                  className="template-name-input"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  placeholder="Template name..."
                />
                <select value={workoutType} onChange={e => setWorkoutType(e.target.value)}>
                  <option value="Weight Training">Weight Training</option>
                  <option value="Cardio">Cardio</option>
                  <option value="HIIT">HIIT</option>
                  <option value="Yoga">Yoga</option>
                  <option value="Stretching">Stretching</option>
                </select>
                <button className="btn-sm btn-danger" style={{marginLeft: 'auto'}} onClick={async () => {
                  if (!window.confirm(`Delete "${templateName}"? This will permanently remove the template and all its sets.`)) return;
                  try {
                    await api.deleteTemplate(selectedTemplate.id);
                    setSelectedTemplate(null);
                    setSections([]);
                    const tpls = await api.getTemplates(profileId);
                    setTemplates(tpls);
                  } catch (e) { alert('Delete failed: ' + e.message); }
                }}>🗑 Delete</button>
              </div>
              <div className="builder-meta">
                <label className="meta-label">Program:</label>
                <select
                  className="meta-select"
                  value={selectedTemplate?.program_id || selectedProgram || ''}
                  onChange={e => {
                    const newPid = +e.target.value;
                    setSelectedTemplate({ ...selectedTemplate, program_id: newPid });
                    setSelectedProgram(newPid);
                  }}
                >
                  <option value="">No program</option>
                  {programs.map(p => <option key={p.id} value={p.id}>{p.program_name}</option>)}
                </select>
              </div>

              {sections.map((section, idx) => (
                <Section
                  key={idx}
                  section={section}
                  sectionIndex={idx}
                  onUpdate={updated => updateSection(idx, updated)}
                  onRemove={() => removeSection(idx)}
                  onAddExercise={() => setShowExerciseSearch({ sectionIdx: idx, exIdx: null })}
                  onPickExercise={(exIdx) => setShowExerciseSearch({ sectionIdx: idx, exIdx })}
                  onAddBlock={() => setShowBlockCreator(idx)}
                  onMoveUp={() => moveSection(idx, -1)}
                  onMoveDown={() => moveSection(idx, 1)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < sections.length - 1}
                  sectionNames={sections.map(s => s.name)}
                  onMoveExerciseToSection={(fromSectionIdx, exIdx, toSectionIdx) => {
                    try {
                      const newSections = sections.map(s => ({ ...s, exercises: [...s.exercises] }));
                      if (!newSections[fromSectionIdx] || !newSections[toSectionIdx]) return;
                      if (exIdx >= newSections[fromSectionIdx].exercises.length) return;
                      const [moved] = newSections[fromSectionIdx].exercises.splice(exIdx, 1);
                      newSections[toSectionIdx].exercises.push(moved);
                      setSections(newSections);
                    } catch (err) { console.error('Move section failed:', err); }
                  }}
                />
              ))}

              <button className="btn-add-section" onClick={addSection}>+ Add Section</button>
            </>
          ) : (
            <div className="hd-wrap">
              {/* Header */}
              <div className="hd-header">
                <div>
                  <div className="hd-greeting">
                    {(() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; })()}{userFirstName ? `, ${userFirstName}` : ''}
                  </div>
                  <div className="hd-sub">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    {templates.length > 0 && ` · ${templates.length} template${templates.length !== 1 ? 's' : ''} in your library`}
                  </div>
                </div>
                <button className="hd-btn-primary" onClick={handleNewProgram}>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  New Program
                </button>
              </div>

              {/* Programs */}
              <div className="hd-sec-hdr">
                <span className="hd-sec-title">Programs</span>
                {programs.length > 0 && <span className="hd-sec-count">{programs.length}</span>}
              </div>
              {programs.length === 0 ? (
                <div className="hd-empty-programs">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{color:'#f97316',marginBottom:12}}><line x1="2" y1="12" x2="22" y2="12"/><rect x="1" y="9" width="3" height="6" rx="1" fill="currentColor" stroke="none"/><rect x="20" y="9" width="3" height="6" rx="1" fill="currentColor" stroke="none"/><rect x="4" y="8" width="2" height="8" rx="1" fill="currentColor" stroke="none"/><rect x="18" y="8" width="2" height="8" rx="1" fill="currentColor" stroke="none"/></svg>
                  <div className="hd-empty-label">No programs yet</div>
                  <div className="hd-empty-sub">Create a program to group your training templates</div>
                  <button className="hd-btn-primary" style={{marginTop:14}} onClick={handleNewProgram}>+ New Program</button>
                </div>
              ) : (
                <div className="hd-programs-grid">
                  {programs.slice(0, 4).map(p => {
                    const count = templates.filter(t => t.program_id === p.id).length;
                    return (
                      <div key={p.id} className={`hd-prog-card ${selectedProgram === p.id ? 'selected' : ''}`} onClick={() => setSelectedProgram(selectedProgram === p.id ? null : p.id)}>
                        <div className="hd-prog-top">
                          <div className="hd-prog-icon">
                            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round"><line x1="2" y1="12" x2="22" y2="12"/><rect x="1" y="9" width="3" height="6" rx="1" fill="#f97316" stroke="none"/><rect x="20" y="9" width="3" height="6" rx="1" fill="#f97316" stroke="none"/><rect x="4" y="8" width="2" height="8" rx="1" fill="#f97316" stroke="none"/><rect x="18" y="8" width="2" height="8" rx="1" fill="#f97316" stroke="none"/></svg>
                          </div>
                          {p.ai_generated && <span className="hd-prog-badge ai">AI Built</span>}
                          {!p.ai_generated && p.split_type && <span className="hd-prog-badge">{p.split_type}</span>}
                        </div>
                        <div className="hd-prog-name">{p.program_name}</div>
                        <div className="hd-prog-meta">
                          {[p.goal, p.days_per_week ? `${p.days_per_week} days/wk` : null].filter(Boolean).join(' · ') || 'No details yet'}
                        </div>
                        <div className="hd-prog-footer">
                          <span className="hd-prog-tcount">{count} template{count !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Templates in the selected program */}
              {selectedProgram && prog && (
                <div className="hd-prog-templates">
                  <div className="hd-sec-hdr" style={{ marginTop: 18 }}>
                    <span className="hd-sec-title">{prog.program_name} — Templates</span>
                    <span className="hd-sec-count">{progTemplates.length}</span>
                    <button className="btn-sm" style={{ marginLeft: 'auto' }} onClick={handleNewTemplate}>+ New</button>
                  </div>
                  {progTemplates.length === 0 ? (
                    <div className="hd-empty-sub" style={{ padding: '6px 2px 4px' }}>No templates in this program yet. Tap “+ New” to create one.</div>
                  ) : (
                    <div className="hd-prog-tmpl-list">
                      {progTemplates.map(t => (
                        <div key={t.id} className="hd-tmpl-card" onClick={() => loadTemplate(t.id)}>
                          <div className="hd-tmpl-name">{t.name}</div>
                          <div className="hd-tmpl-meta">{t.set_count || 0} sets</div>
                          <div className="hd-tmpl-open">Open in builder →</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Recent Templates */}
              {templates.length > 0 && (
                <>
                  <div className="hd-sec-hdr" style={{marginTop: 28}}>
                    <span className="hd-sec-title">Recent Templates</span>
                  </div>
                  <div className="hd-tmpl-scroll">
                    {[...templates].sort((a, b) => b.id - a.id).slice(0, 6).map(t => (
                      <div key={t.id} className="hd-tmpl-card" onClick={() => loadTemplate(t.id)}>
                        <div className="hd-tmpl-name">{t.name}</div>
                        <div className="hd-tmpl-meta">
                          {t.set_count || 0} sets{t.program_name ? ` · ${t.program_name}` : ''}
                        </div>
                        <div className="hd-tmpl-open">Open in builder →</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Quick Actions */}
              <div className="hd-sec-hdr" style={{marginTop: 28}}>
                <span className="hd-sec-title">Quick Actions</span>
              </div>
              <div className="hd-qa-grid">
                <div className="hd-qa-card" onClick={handleNewProgram}>
                  <div className="hd-qa-icon" style={{background:'rgba(249,115,22,0.1)'}}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  </div>
                  <div className="hd-qa-label">New Program</div>
                </div>
                <div className="hd-qa-card" onClick={async () => {
                  if (!selectedProgram) { alert('Select a program first'); return; }
                  const name = prompt('Template name:');
                  if (!name) return;
                  try {
                    const tpl = await api.createTemplate(selectedProgram, name, 'Weight Training');
                    setTemplates(await api.getTemplates(profileId));
                    loadTemplate(tpl.id);
                  } catch(e) { alert(e.message); }
                }}>
                  <div className="hd-qa-icon" style={{background:'rgba(249,115,22,0.08)'}}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                  </div>
                  <div className="hd-qa-label">New Template</div>
                </div>
                <div className="hd-qa-card" style={{opacity:0.5,cursor:'default'}}>
                  <div className="hd-qa-icon" style={{background:'rgba(99,102,241,0.1)'}}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  </div>
                  <div className="hd-qa-label">AI Coach</div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      {showExerciseSearch !== null && (
        <ExerciseSearch
          onSelect={ex => {
            if (showExerciseSearch && showExerciseSearch.exIdx != null) {
              handleFillSlot(showExerciseSearch.sectionIdx, showExerciseSearch.exIdx, ex);
            } else {
              handleAddExercise(showExerciseSearch.sectionIdx, ex);
            }
          }}
          onClose={() => setShowExerciseSearch(null)}
        />
      )}
      {showBlockCreator !== null && (
        <BlockCreator
          onCreateBlock={(count, rounds, type) => handleCreateBlock(showBlockCreator, count, rounds, type)}
          onClose={() => setShowBlockCreator(null)}
        />
      )}
    </div>
  );
}

export default App;
